import { useLayoutEffect, useRef } from 'react';

const TEXT_CONTROL = 'input:not([type="hidden"]), textarea, [contenteditable]';
const CLICK_CONTROL = 'button, select, [role="combobox"]';
const OWN_CONTROL =
  'input:not([type="hidden"]), textarea, select, button, [contenteditable], [role="radiogroup"], [role="group"], [role="combobox"]';

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

function clearTags(control) {
  control.removeAttribute('data-gap-anchor');
  control.removeAttribute('data-gap-also');
  control.removeAttribute('data-gap-empty');
}

const REACT_SELECT_INPUT = 'input[role="combobox"], input[aria-autocomplete]';
const REACT_SELECT_BOX = /^css-[\w-]+-control$|__control$/;

function reactSelectBox(input, root) {
  if (!input.matches(REACT_SELECT_INPUT)) return input;
  for (let node = input.parentElement; node && node !== root; node = node.parentElement) {
    if ([...node.classList].some((name) => REACT_SELECT_BOX.test(name))) return node;
  }
  return input;
}

function locateControl(root, name) {
  const named = [...root.querySelectorAll('[id]')].find((node) => node.id === name);
  if (named) {
    if (named.matches(OWN_CONTROL)) return named;
    return named.querySelector(TEXT_CONTROL) ?? named.querySelector(CLICK_CONTROL) ?? named;
  }
  return (
    root.querySelector('.ProseMirror') ??
    root.querySelector(TEXT_CONTROL) ??
    root.querySelector(CLICK_CONTROL)
  );
}

function findControl(root, name) {
  const control = locateControl(root, name);
  return control ? reactSelectBox(control, root) : control;
}

export default function ControlAnchor({ rootRef, name, anchor, empty, value }) {
  const tagged = useRef(null);
  const blank = empty ?? isBlank(value);
  const list = (Array.isArray(anchor) ? anchor : [anchor]).filter(Boolean);
  const main = list[0] ?? null;
  const also = list[1] ?? null;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !main) return;
    const control = findControl(root, name);
    if (!control) return;
    if (tagged.current && tagged.current !== control) clearTags(tagged.current);
    tagged.current = control;
    if (control.getAttribute('data-gap-anchor') !== main)
      control.setAttribute('data-gap-anchor', main);
    if (also && control.getAttribute('data-gap-also') !== also)
      control.setAttribute('data-gap-also', also);
    if (!also) control.removeAttribute('data-gap-also');
    if (control.getAttribute('data-gap-empty') !== String(blank))
      control.setAttribute('data-gap-empty', String(blank));
  });

  useLayoutEffect(
    () => () => {
      if (tagged.current) clearTags(tagged.current);
      tagged.current = null;
    },
    [main, also],
  );

  return null;
}
